import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { TrafficChannel } from '@ads-manager/shared';

interface ChannelState {
  selectedChannel: TrafficChannel | null;
  availableChannels: TrafficChannel[];

  // Actions
  setSelectedChannel: (channel: TrafficChannel | null) => void;
  setAvailableChannels: (channels: TrafficChannel[]) => void;
  initializeChannel: (channels: TrafficChannel[]) => void;
  clearChannel: () => void;
}

export const useChannelStore = create<ChannelState>()(
  persist(
    (set, get) => ({
      selectedChannel: null,
      availableChannels: [],

      setSelectedChannel: (channel: TrafficChannel | null) => {
        set({ selectedChannel: channel });
      },

      setAvailableChannels: (channels: TrafficChannel[]) => {
        set({ availableChannels: channels });
      },

      initializeChannel: (channels: TrafficChannel[]) => {
        const { selectedChannel } = get();
        set({ availableChannels: channels });

        // If no channel selected or current selection not in available channels,
        // select the first available channel
        if (!selectedChannel || !channels.find((c) => c.id === selectedChannel.id)) {
          set({ selectedChannel: channels.length > 0 ? channels[0] : null });
        }
      },

      clearChannel: () => {
        set({ selectedChannel: null, availableChannels: [] });
      },
    }),
    {
      name: 'channel-storage',
      partialize: (state) => ({
        selectedChannel: state.selectedChannel,
      }),
      // Migration: handle old string-based Channel values
      migrate: (persistedState: unknown, _version: number) => {
        const state = persistedState as { selectedChannel?: unknown };
        // If selectedChannel is a string (old format), clear it
        if (state && typeof state.selectedChannel === 'string') {
          return { selectedChannel: null };
        }
        return state as ChannelState;
      },
      version: 1,
    }
  )
);

export default useChannelStore;
